import React, { useEffect } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import * as Actions from "us.collection.input-output/actions";
import { connect, ConnectedProps } from "react-redux";
import { UploadOutlined, FileSearchOutlined } from "us.icons";
import { Uploader, Summary, ImportResult } from "./Component";
import { ImportTypes } from "./Constants";
import _ from "lodash";
import { handleGoBack } from "us.helper/utility";
import { getFileTypeMappings } from "./Functions";
import { FileConverterValidationSchema } from "./Validations";
import { ImportAndPreviewFile } from "./Repository";
import { RootState } from "us.helper/types";
import { useNavigate } from "react-router-dom";
import "./Home.scss";

const {
  $Affix,
  $PageHeader,
  $Select,
  $Button,
  $Form,
  $Divider,
  $Popconfirm,
  $MessageBox,
} = Common.Components;

/**
 * @description - FileConverter component for uploading, previewing, and importing files with creditor and file type selection.
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @link Task - https://unicorn-solutions.atlassian.net/browse/UBX4-25356
 * @since 17/06/2025
 */
const FileConverter: React.FC<PropsFromRedux> = (props) => {
  const {
    fileList,
    initialValues,
    previewResult,
    importResult,
    isPreviewLoading,
    isImporting,
    creditorsAndProfiles,
    previewFile,
    importFile,
    resetFileImport,
    updateFiles,
    getMappingProfiles,
  } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    getMappingProfiles && getMappingProfiles({});
    resetFileImport && resetFileImport({});
  }, []);

  const handleSubmit = (values: any, type: ImportTypes) => {
    if (!fileList || !values.creditor || !values.fileType) {
      $MessageBox(
        "error",
        "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.REQUIRED_FIELDS_ARE_MISSING",
        "",
        ""
      );
      return;
    }

    const saveParams = ImportAndPreviewFile.call({
      ...values,
      fileList,
      creditors: creditorsAndProfiles.data,
    });
    if (type == ImportTypes.PREVIEW) {
      previewFile && previewFile(saveParams);
    } else {
      importFile && importFile(saveParams);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnChange
      validateOnBlur
      validationSchema={FileConverterValidationSchema}
      onSubmit={() => {}}
    >
      {({ values, setFieldValue, isValid }: any) => (
        <>
          <div className="space-content">
            <$Affix offsetTop={48}>
              <div className="page-header header-border">
                <div className="d-flex align-items-center">
                  <$PageHeader
                    className="px-0"
                    onBack={() => handleGoBack(navigate)}
                    title={t(
                      "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.FILE_CONVERTER"
                    )}
                  />
                </div>
              </div>
            </$Affix>
            <div className="imp-file_content mt-4">
              {_.isEmpty(previewResult) && _.isEmpty(importResult) && (
                <$Form layout="vertical">
                  <$Select
                    name="creditor"
                    dataTestid={"select-creditor"}
                    formitem={{
                      label: t(
                        "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.CREDITOR"
                      ),
                    }}
                    placeholder={t(
                      "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.SELECT_CREDITOR"
                    )}
                    size="small"
                    options={creditorsAndProfiles.data}
                    optionText="creditorName"
                    optionValue="creditorId"
                    allOption={false}
                    autoFocus={true}
                    loading={creditorsAndProfiles.isLoading}
                    disabled={
                      isPreviewLoading ||
                      isImporting ||
                      creditorsAndProfiles.isLoading
                    }
                    tabIndex={1}
                    className="mb-1"
                    required
                    onChange={() => setFieldValue("fileType", "")}
                  />
                  <$Select
                    name="fileType"
                    dataTestid={"select-file-type"}
                    formitem={{
                      label: t(
                        "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.FILE_TYPE"
                      ),
                    }}
                    placeholder={t(
                      "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.SELECT_FILE_TYPE"
                    )}
                    size="small"
                    options={getFileTypeMappings(
                      creditorsAndProfiles.data,
                      values.creditor
                    )}
                    optionExtra={{
                      extraNumber: "mappingProfileDisplayName",
                      hideSeparator: true,
                      extraName: "mappingProfileName",
                    }}
                    optionValue="inputFileType"
                    allOption={false}
                    disabled={
                      isPreviewLoading ||
                      isImporting ||
                      creditorsAndProfiles.isLoading
                    }
                    tabIndex={2}
                    className="mb-1"
                    required
                  />

                  <Uploader
                    fileList={fileList}
                    onChangeFile={(files: Array<any>) =>
                      updateFiles && updateFiles(files)
                    }
                    uploadRules={{
                      formats: ".txt, .dat, .csv, .json, .xml",
                      maxCount: 1,
                      maxSize: 5,
                    }}
                    disabled={isPreviewLoading || isImporting}
                  />
                </$Form>
              )}
              {!_.isEmpty(previewResult) && <Summary />}
              {!_.isEmpty(importResult) && <ImportResult />}

              <$Divider />

              <div className="d-flex">
                {_.isEmpty(importResult) && (
                  <$Popconfirm
                    title={t("COMMON.SURE_TO_PROCEED_?")}
                    placement="topLeft"
                    data-testid="pop-confirm"
                    okButtonProps={{
                      dataTestid: "pop-confirm-ok",
                    }}
                    onConfirm={() =>
                      isValid && handleSubmit(values, ImportTypes.IMPORT)
                    }
                    okText={t("COMMON.YES")}
                    cancelText={t("COMMON.NO")}
                    disabled={isImporting}
                  >
                    <$Button
                      className="mr-2"
                      type="primary"
                      size="large"
                      block
                      data-testid="import-btn"
                      loading={isImporting}
                      disabled={
                        isImporting ||
                        isPreviewLoading ||
                        !isValid ||
                        fileList.length == 0
                      }
                      icon={<UploadOutlined />}
                    >
                      {t("US.COLLECTION.COMMON:COMMON.IMPORT")}
                    </$Button>
                  </$Popconfirm>
                )}

                {_.isEmpty(previewResult) && _.isEmpty(importResult) && (
                  <$Button
                    type="primary"
                    size="large"
                    block
                    data-testid="preview-btn"
                    loading={isPreviewLoading}
                    disabled={
                      isImporting ||
                      isPreviewLoading ||
                      !isValid ||
                      fileList.length == 0
                    }
                    onClick={() =>
                      isValid && handleSubmit(values, ImportTypes.PREVIEW)
                    }
                    icon={<FileSearchOutlined />}
                  >
                    {t("US.COLLECTION.COMMON:COMMON.PREVIEW")}
                  </$Button>
                )}

                {(!_.isEmpty(previewResult) || !_.isEmpty(importResult)) && (
                  <$Button
                    type="default"
                    size="large"
                    data-testid="cancel-import"
                    block
                    disabled={isImporting}
                    onClick={() => {
                      resetFileImport && resetFileImport({});
                    }}
                  >
                    {!_.isEmpty(importResult)
                      ? t("US.COLLECTION.COMMON:COMMON.OK")
                      : t("US.COLLECTION.COMMON:COMMON.CANCEL")}
                  </$Button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </Formik>
  );
};
const mapStateToProps = (state: RootState) => {
  const { fileConverter } = state;

  const {
    initialValues,
    preview,
    importFiles,
    fileList,
    creditorsAndProfiles,
  } = fileConverter;

  return {
    initialValues,
    fileList,
    previewResult: preview.data,
    importResult: importFiles.data,
    isPreviewLoading: preview.isLoading,
    isImporting: importFiles.isLoading,
    creditorsAndProfiles: creditorsAndProfiles,
  };
};

const { preview, importFiles, mappingProfiles } = Actions.fileConverter;
const { save, reset, updateFiles } = preview;

const mapDispatchToProps = {
  updateFiles,
  previewFile: save,
  resetFileImport: reset,
  importFile: importFiles.save,
  getMappingProfiles: mappingProfiles.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(FileConverter);
